import { useState, useEffect } from 'react';

interface UTMData {
  utmTrackingId: number;
  shouldSendGA4: boolean;
  utmParams: Record<string, string | null>;
  isNew: boolean;
}

// Declare gtag function
declare function gtag(command: string, targetId: string, config: string, callback: (clientId: string) => void): void;

// Parse UTM params from a URLSearchParams instance
const extractUTMParams = (params: URLSearchParams) => ({
  gad_campaignid: params.get('gad_campaignid'),
  gad_source: params.get('gad_source'),
  gbraidid: params.get('gbraidid'),
  gclid: params.get('gclid'),
  utm_source: params.get('utm_source'),
  utm_medium: params.get('utm_medium'),
  utm_campaign: params.get('utm_campaign'),
  utm_term: params.get('utm_term'),
  utm_content: params.get('utm_content'),
  fbclid: params.get('fbclid'),
});

// Custom hook for UTM tracking
export const useUTMTracking = () => {
  const [utmData, setUTMData] = useState<UTMData | null>(null);
  const [ga4ClientId, setGA4ClientId] = useState<string | null>(null);

  useEffect(() => {
    const rawSearch = window.location.search;
    let referralUrl: string | null = null;
    let utmParams: Record<string, string | null>;

    // Detect referral URL pattern: ?https://eclipsetint.com.au/page?utm_source=...
    if (rawSearch.startsWith('?http')) {
      referralUrl = rawSearch.substring(1); // strip leading ?
      console.log('🔗 Website referral detected:', referralUrl);

      try {
        const refUrl = new URL(referralUrl);
        utmParams = extractUTMParams(refUrl.searchParams);
      } catch {
        // URL parsing failed — treat entire string as referral with no UTMs
        utmParams = extractUTMParams(new URLSearchParams());
      }

      // If referral URL has no UTMs, create synthetic tracking data
      const hasEmbeddedUTMs = Object.values(utmParams).some(v => v);
      if (!hasEmbeddedUTMs) {
        utmParams.utm_source = 'eclipse-tint-website';
        utmParams.utm_medium = 'referral';
        utmParams.utm_campaign = 'website-referral';
        console.log('✅ No UTMs in referral URL, using synthetic:', utmParams);
      }
    } else {
      // Standard UTM parameter extraction from query string
      const urlParams = new URLSearchParams(rawSearch);
      utmParams = extractUTMParams(urlParams);
    }

    // Check if any UTM parameters exist OR we have a referral URL
    const hasUTMParams = Object.values(utmParams).some(value => value);
    const isWebsiteReferral = referralUrl !== null;

    if (!hasUTMParams && !isWebsiteReferral) {
      console.log('No UTM parameters or website referral found, skipping tracking');
      return;
    }

    console.log('UTM parameters found, initializing tracking:', utmParams);

    // Capture UTM parameters from URL
    const captureUTMData = async () => {
      try {
        // Send UTM data to backend for tracking
        const response = await fetch('/api/utm/capture', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            utmParams,
            pageUrl: window.location.href,
            referrer: document.referrer,
            referralUrl,
          })
        });

        const result = await response.json();
        
        if (result.success) {
          setUTMData({
            utmTrackingId: result.utmTrackingId,
            shouldSendGA4: result.shouldSendGA4,
            utmParams,
            isNew: result.isNew
          });
        }
      } catch (error) {
        console.error('Error capturing UTM data:', error);
      }
    };

    // Get GA4 Client ID (only when we have UTM params)
    const getGA4ClientId = () => {
      // Try to get from gtag if available
      if (typeof window !== 'undefined' && typeof gtag !== 'undefined') {
        gtag('get', process.env.REACT_APP_GA4_MEASUREMENT_ID || 'G-SBPK84T1KF', 'client_id', (clientId: string) => {
          setGA4ClientId(clientId);
        });
      } else {
        // Fallback: generate client ID
        const clientId = `${Date.now()}.${Math.random().toString(36).substr(2, 9)}`;
        setGA4ClientId(clientId);
      }
    };

    captureUTMData();
    getGA4ClientId();
  }, []);

  return { utmData, ga4ClientId };
};

export default useUTMTracking;
